import {
  Module,
  VuexModule,
  Mutation,
  Action,
  config
} from "vuex-module-decorators";
import { PropertiesData } from "@/types/tree";
import VueSimpleDeepset from "@/util/vue-simple-deepset";
import { CSSSelector } from "@/types/selectors";
import { throttlePropertyChange } from "@/util/throttle-property-change";
import {
  PresetStorePropertyChange,
  SelectorStorePropertyChange,
  SettingStorePropertyChange,
  VariableStorePropertyChange
} from "@/types/store-property-change";
import { GlobalColor, GlobalColorsV } from "@/types/colors";
import lodash, { isEqual } from "lodash";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import defensiveCopy from "@/util/defensive-copy";
import { breakdanceUndo } from "@/lib/undo/undo";
import { TypographyPreset } from "@/types/typography";
import { generateTransactionKeyFromGenericStorePropertyChange } from "@/lib/undo/transaction-key.utils";
import {
  GlobalSettingsRevision,
  OxySelectorsRevision,
  PresetsRevision,
  SelectorsRevision
} from "@/types/revisions";
import { compare } from "rfc6902-json-diff";
import { applyPatchWithVueReactivity } from "@/util/fast-json-patch-reactive-ops";
import { Variable, VariablesRevision } from "@/oxygen/variables/lib/types";
import { ElementPreset } from "@/types/presets";
import { AISettings } from "@/lib/ai/types";
import { OxySelector } from "@/oxygen/classes/lib/types";
import { getDefaultAiSettings } from "@/lib/ai/ai";
import { getBuilderSelectorPrefix } from "@/lib/canvas/reacting-to-global-store-changes";

/**
 * IMPORTANT:
 * READ & UNDERSTAND THE FOLLOWING BEFORE ADDING
 * PROPERTIES TO THE DOCUMENT OR GLOBAL STORE
 *
 * <see the comment in the document store>
 */

export const GLOBAL_SETTINGS_PATHS = {
  globalColors: "settings.colors.palette.colors",
  globalGradients: "settings.colors.palette.gradients",
  typographyPresets: "settings.typography.global_typography.typography_presets",
  globalVariables: "settings.local_variables",
  globalButtons: "settings.buttons"
};

config.rawError = true;
@Module({ name: "global", namespaced: true })
export default class GlobalModule extends VuexModule {
  // Global settings
  globalSettings: PropertiesData = {};
  untouchedGlobalSettings: PropertiesData = {};

  // CSS selectors
  cssSelectors: CSSSelector[] = [];
  untouchedCssSelectors: CSSSelector[] = [];

  // Variables
  variables: Variable[] = [];
  untouchedVariables: Variable[] = [];
  variablesCollections: string[] = [];

  // Presets
  presets: ElementPreset[] = [];
  untouchedPresets: ElementPreset[] = [];

  // Oxy Selectors
  oxySelectors: OxySelector[] = [];
  untouchedOxySelectors: OxySelector[] = [];
  oxySelectorsCollections: string[] = [];

  untouchedAiSettings: AISettings | null = getDefaultAiSettings();
  aiSettings: AISettings = getDefaultAiSettings();

  /*
   * This is the globalProperties object that is used to generate the CSS.
   * Add any global properties here that are used to generate the CSS.
   */
  get globalProperties() {
    return {
      ...this.globalSettings,
      variables: this.variables,
      builderPrefix: getBuilderSelectorPrefix()
    };
  }

  get globalColors(): GlobalColor[] {
    const maybeGlobalColors = lodash.get(
      this.globalSettings,
      GLOBAL_SETTINGS_PATHS.globalColors
    );

    return pipe(
      maybeGlobalColors,
      GlobalColorsV.decode,
      fold(
        () => [],
        globalColors => globalColors
      )
    );
  }

  get globalGradients(): GlobalColor[] {
    const maybeGlobalGradients = lodash.get(
      this.globalSettings,
      GLOBAL_SETTINGS_PATHS.globalGradients
    );

    return pipe(
      maybeGlobalGradients,
      GlobalColorsV.decode,
      fold(
        () => [],
        globalColors => globalColors
      )
    );
  }

  get settingsHasUnsavedChangesPresent() {
    return !isEqual(
      defensiveCopy(this.untouchedGlobalSettings),
      defensiveCopy(this.globalSettings)
    );
  }

  get selectorsHasUnsavedChangesPresent() {
    return !isEqual(
      defensiveCopy(this.untouchedCssSelectors),
      defensiveCopy(this.cssSelectors)
    );
  }

  get variablesHasUnsavedChangesPresent() {
    return !isEqual(
      defensiveCopy(this.untouchedVariables),
      defensiveCopy(this.variables)
    );
  }

  get presetsHasUnsavedChangesPresent() {
    return !isEqual(
      defensiveCopy(this.untouchedPresets),
      defensiveCopy(this.presets)
    );
  }

  get oxySelectorsHasUnsavedChangesPresent() {
    return !isEqual(
      defensiveCopy(this.untouchedOxySelectors),
      defensiveCopy(this.oxySelectors)
    );
  }

  get aiSettingsHasUnsavedChangesPresent() {
    return !isEqual(
      defensiveCopy(this.untouchedAiSettings),
      defensiveCopy(this.aiSettings)
    );
  }

  @Action
  setGlobalColors(colors: GlobalColor[]) {
    this.throttledGlobalSettingsPropertyChange({
      path: GLOBAL_SETTINGS_PATHS.globalColors,
      value: colors,
      meta: {
        snapshotLabel: "Change Global Colors"
      }
    });
  }

  @Action
  addGlobalColor(color: GlobalColor) {
    this.throttledGlobalSettingsPropertyChange({
      path: GLOBAL_SETTINGS_PATHS.globalColors,
      value: [...defensiveCopy(this.globalColors), color],
      meta: {
        snapshotLabel: "Add Global Color"
      }
    });
  }

  @Action
  addGlobalGradient(color: GlobalColor) {
    this.throttledGlobalSettingsPropertyChange({
      path: GLOBAL_SETTINGS_PATHS.globalGradients,
      value: [...defensiveCopy(this.globalGradients), color],
      meta: {
        snapshotLabel: "Add Global Gradient"
      }
    });
  }

  @Action
  updateGlobalSettingsPerPath(payload: any) {
    this.unthrottledGlobalSettingsPropertyChange({
      path: payload.path,
      value: payload.value,
      meta: {
        snapshotLabel: "Onboarding: Update Global Settings"
      }
    });
  }
  // Selectors

  @Action
  throttledSelectorPropertyChange(change: SelectorStorePropertyChange) {
    throttlePropertyChange(
      `${change.selector.name}${change.path}`,
      this.unthrottledSelectorPropertyChangeAction,
      change,
      "selectorSettings"
    );
  }

  @Action
  unthrottledSelectorPropertyChangeAction(change: SelectorStorePropertyChange) {
    breakdanceUndo.transaction(
      () => {
        this.unthrottledSelectorPropertyChange(change);
      },
      change.meta.snapshotLabel,
      generateTransactionKeyFromGenericStorePropertyChange(
        change,
        "SelectorProperty"
      )
    );
  }

  @Mutation
  unthrottledSelectorPropertyChange({
    selector,
    path,
    value
  }: SelectorStorePropertyChange) {
    VueSimpleDeepset(selector, path, value);
  }

  // Global Variables

  @Action
  throttledVariablePropertyChange(change: VariableStorePropertyChange) {
    throttlePropertyChange(
      `${change.variable.cssVariableName}${change.path}`,
      this.unthrottledVariablePropertyChangeAction,
      change,
      "variableSettings"
    );
  }

  // Presets
  @Action
  throttledPresetPropertyChange(change: PresetStorePropertyChange) {
    throttlePropertyChange(
      `${change.preset.id}${change.path}`,
      this.unthrottledPresetPropertyChangeAction,
      change,
      "presetSettings"
    );
  }

  @Mutation
  unthrottledVariablePropertyChangeAction(change: VariableStorePropertyChange) {
    breakdanceUndo.transaction(
      () => {
        this.unthrottledVariablePropertyChange(change);
      },
      change.meta.snapshotLabel,
      generateTransactionKeyFromGenericStorePropertyChange(
        change,
        "VariableProperty"
      )
    );
  }

  @Action
  unthrottledPresetPropertyChangeAction(change: PresetStorePropertyChange) {
    breakdanceUndo.transaction(
      () => {
        this.unthrottledPresetPropertyChange(change);
      },
      change.meta.snapshotLabel,
      generateTransactionKeyFromGenericStorePropertyChange(
        change,
        "PresetProperty"
      )
    );
  }

  @Mutation
  unthrottledVariablePropertyChange({
    variable,
    path,
    value
  }: VariableStorePropertyChange) {
    VueSimpleDeepset(variable, path, value);
  }

  @Mutation
  unthrottledPresetPropertyChange({
    preset,
    path,
    value
  }: PresetStorePropertyChange) {
    VueSimpleDeepset(preset, path, value);
  }

  // Global settings

  @Action
  throttledGlobalSettingsPropertyChange({
    path,
    value,
    meta
  }: SettingStorePropertyChange) {
    throttlePropertyChange(
      path,
      this.unthrottledGlobalSettingsPropertyChangeAction,
      {
        path,
        value,
        meta
      },
      "globalSettings"
    );
  }

  @Action
  protected unthrottledGlobalSettingsPropertyChangeAction(
    change: SettingStorePropertyChange
  ): void {
    // TODO move breakdanceUndo.transaction-related logic somehow to BreakdanceUndo?
    breakdanceUndo.transaction(
      () => {
        this.unthrottledGlobalSettingsPropertyChange(change);
      },
      change.meta.snapshotLabel,
      generateTransactionKeyFromGenericStorePropertyChange(
        change,
        "GlobalSettings"
      )
    );
  }

  @Mutation
  unthrottledGlobalSettingsPropertyChange({
    path,
    value
  }: SettingStorePropertyChange) {
    VueSimpleDeepset(this.globalSettings, path, value);
  }

  @Mutation
  setGlobalSettings(globalSettings: PropertiesData) {
    // TODO find where PHP sends globalSettings as [], then use stdObject, then ditch this logic
    // TODO VueSimpleDeepset isn't needed here
    // blank global settings should be a {}... but the way PHP works when JSON encoding, that's tricky
    // so we enforce it to be a {} here (since we can't use VueSimpleDeepset on an array / falsey value)
    // todo: maybe better to make VueSimpleDeepset convert undefined and shit to object?
    // or maybe an implicit conversion would just allow bugs to slip through that are really bugs elsewhere?
    if (globalSettings) {
      if (
        !globalSettings ||
        (Array.isArray(globalSettings) && globalSettings.length === 0)
      ) {
        VueSimpleDeepset(this, "globalSettings", {});
      } else {
        const globalSettingsDiff = compare(this.globalSettings, globalSettings);
        applyPatchWithVueReactivity(this.globalSettings, globalSettingsDiff);
      }
    }
  }

  @Mutation
  setAiSettings(aiSettings: AISettings) {
    this.aiSettings = aiSettings;
  }

  @Mutation
  restoreGlobalSettingsRevision(revision: GlobalSettingsRevision) {
    this.globalSettings = defensiveCopy(revision.globalSettings);
  }

  @Mutation
  restoreSelectorsRevision(revision: SelectorsRevision) {
    this.cssSelectors = defensiveCopy(revision.selectors);
  }

  @Mutation
  restoreOxySelectorsRevision(revision: OxySelectorsRevision) {
    this.oxySelectors = defensiveCopy(revision.oxySelectors);
  }

  @Mutation
  restoreVariablesRevision(revision: VariablesRevision) {
    this.variables = defensiveCopy(revision.variables);
  }

  @Mutation
  restorePresetsRevision(revision: PresetsRevision) {
    this.presets = defensiveCopy(revision.presets);
  }

  @Mutation
  setSelectors(selectors: CSSSelector[]) {
    this.cssSelectors = selectors;
  }

  @Mutation
  setVariables(variables: Variable[]) {
    this.variables = variables;
  }

  @Mutation
  setVariablesCollections(collections: string[]) {
    this.variablesCollections = collections;
  }

  @Mutation
  setPresets(presets: ElementPreset[]) {
    this.presets = presets;
  }

  @Mutation
  setOxygenSelectors(selectors: OxySelector[]) {
    this.oxySelectors = selectors;
  }

  @Mutation
  setOxygenSelectorsCollections(collections: string[]) {
    this.oxySelectorsCollections = collections;
  }

  get typographyPresets(): TypographyPreset[] | undefined {
    return lodash.get(
      this.globalSettings,
      GLOBAL_SETTINGS_PATHS.typographyPresets,
      []
    );
  }

  @Mutation
  markCurrentGlobalSettingsAsUntouched() {
    this.untouchedGlobalSettings = defensiveCopy(this.globalSettings);
  }

  @Mutation
  markCurrentSelectorsAsUntouched() {
    this.untouchedCssSelectors = defensiveCopy(this.cssSelectors);
  }

  @Mutation
  markCurrentVariablesAsUntouched() {
    this.untouchedVariables = defensiveCopy(this.variables);
  }

  @Mutation
  markCurrentPresetsAsUntouched() {
    this.untouchedPresets = defensiveCopy(this.presets);
  }

  @Mutation
  markCurrentOxySelectorsAsUntouched() {
    this.untouchedOxySelectors = defensiveCopy(this.oxySelectors);
  }

  @Mutation
  markCurrentAiSettingsAsUntouched() {
    this.untouchedAiSettings = defensiveCopy(this.aiSettings);
  }
}
